/**
 * FirebaseProvider wired to Pathwright context (integrations.firebase).
 * Use when the app tree has PathwrightContextProvider above this.
 */
import { ReactNode, useMemo } from "react"
import {
  FirebaseProvider as FirebaseProviderBase,
  type FirebaseClientConfig
} from "@pathwright/utils"
import { safeParseAuthToken } from "@pathwright/utils/src/token/utils"
import { usePathwrightContext } from "../pathwright/PathwrightContext"

/** Per-user app name so Auth persistence does not collide across users in the same origin. */
function firebaseAppNameForUserId(userId: number): string {
  return `pathwright-user-${userId}`
}

function resolveFirebaseAppName(meId: number | null | undefined): string | undefined {
  if (meId != null) return firebaseAppNameForUserId(meId)
  const parsed = safeParseAuthToken()
  return parsed.success
    ? firebaseAppNameForUserId(parsed.data.sub)
    : undefined
}

export const FirebaseProvider = ({ children }: { children: ReactNode }) => {
  const { integrations, me } = usePathwrightContext()
  const firebaseConfig: FirebaseClientConfig | undefined =
    integrations?.firebase
      ? {
          url: integrations.firebase.url ?? undefined,
          client_api_key: integrations.firebase.client_api_key ?? undefined
        }
      : undefined
  const firebaseAuthToken = integrations?.firebase?.auth_token ?? undefined

  // Named by Pathwright user so same-origin windows (e.g. sandbox shell + canvas)
  // keep separate Firebase Auth when they are different users — no sandbox hook.
  const appName = useMemo(() => resolveFirebaseAppName(me?.id), [me?.id])

  return (
    <FirebaseProviderBase
      firebaseConfig={firebaseConfig}
      firebaseAuthToken={firebaseAuthToken}
      appName={appName}
    >
      {children}
    </FirebaseProviderBase>
  )
}
